import { z } from 'zod';
import { FOOD_CUISINES } from './foods.js';
import { DIET_TYPES } from './users.js';
/**
 * The curated meal grammar.
 *
 * One row is one **shape** an Indian household serves a meal in — how many of
 * each `mealRole` a breakfast, lunch, dinner, mid-morning or snack may carry.
 * Plan generation reads these rows on every run and merges them with the
 * seeded constants in the API's `meal-patterns.ts` **one slug at a time**: a
 * slug with an activated or retired version here is this table's answer, and
 * every other seeded shape is untouched. So an empty screen is a working
 * product, not a broken one, and activating one shape changes that one shape
 * rather than switching the rest of the grammar off.
 *
 * **The boundary is the thing to understand before editing one.** A pattern
 * says how many of each role fit on the plate and nothing else:
 *
 * - It never declares a `beverage`, `condiment` or `dessert` component. Those
 *   are already capped at one per meal (and one dessert per day) by rules that
 *   are not this one, so a pattern counting them would report the same lassi
 *   twice and could contradict the cap outright. The API refuses such a
 *   component, and this schema's own vocabulary excludes them.
 * - It never says "this meal needs an anchor". A different rule owns that
 *   sentence. A pattern only ever fires on a meal carrying an item with
 *   **nowhere to go**, never on one that is merely short of something.
 * - Patterns are **alternatives**: a meal has to match one shape for its slot,
 *   so adding a pattern always widens what passes and retiring one narrows it.
 *
 * Versioned rather than mutable, like a food's recipe: editing inserts version
 * N+1 and retires the version it replaces, so "who decided a lunch is one main
 * dish" survives the next edit.
 */
/** Mirrors `MealPatternStatus`. Only the two middle values are enforced or shown to the model. */
export const MEAL_PATTERN_STATUSES = [
    'draft',
    'internal_review',
    'active_provisional',
    'expert_reviewed',
    'retired',
];
/** The two statuses plan generation actually enforces and renders into the prompt. */
export const LIVE_MEAL_PATTERN_STATUSES = ['active_provisional', 'expert_reviewed'];
/** Mirrors `MealType`, which is what the row's `slot` column is. */
export const MEAL_PATTERN_SLOTS = ['breakfast', 'mid_morning', 'lunch', 'snack', 'dinner'];
/**
 * What a component may ask for.
 *
 * `MEAL_ROLES` minus the three the per-meal caps already own, plus `any_dish`
 * for "a cooked dish, whatever role it was given" — which exists because a
 * tiffin breakfast is one dish and the catalog disagrees about which role that
 * dish has (idli is a staple, poha a main, besan cheela a protein anchor).
 */
export const PATTERN_RIDE_ALONG_ROLES = ['beverage', 'condiment', 'dessert'];
/**
 * Written out rather than derived from `MEAL_ROLES` by filtering: a derived
 * tuple loses its literal element types and zod cannot close an enum over it.
 * `satisfies` keeps the relationship checked, and the contract's own test
 * pins it against `MEAL_ROLES` so a role added there cannot silently go
 * missing here.
 */
export const PATTERN_COMPONENT_ROLES = [
    'main',
    'protein_anchor',
    'staple',
    'vegetable',
    'side',
    'snack',
    'any_dish',
];
/**
 * One component slot.
 *
 * `required` and `min` say what a meal must carry to be read as *this* shape;
 * `max` is what actually rejects a meal, because a pattern only fires on an
 * item with nowhere to go.
 */
export const adminMealPatternComponentSchema = z.object({
    role: z.enum(PATTERN_COMPONENT_ROLES),
    required: z.boolean(),
    min: z.number().int().min(0).max(6),
    max: z.number().int().min(1).max(6),
});
/**
 * Roles that also satisfy a component asking for the key role, on this plate
 * only — `{"vegetable": ["protein_anchor"]}` is a dal standing in for the
 * sabzi beside the roti. Ride-along roles are excluded on both sides, for the
 * reason components exclude them.
 */
export const adminMealPatternSubstitutionsSchema = z.record(z.enum(PATTERN_COMPONENT_ROLES), z.array(z.enum(PATTERN_COMPONENT_ROLES)).min(1));
/**
 * Where a shape in the list came from.
 *
 * `seeded` shapes are the ones the API ships with. They are in force, they are
 * what the model is asked for, and they have no database row — so no id, no
 * version and no timestamps. They are listed anyway, because a screen called
 * Meal patterns that shows nothing while twelve patterns govern every
 * generated meal is a screen that answers its own question wrongly.
 *
 * Overriding one means creating a `curated` shape with the same name.
 */
export const MEAL_PATTERN_ORIGINS = ['seeded', 'curated'];
export const adminMealPatternSchema = z.object({
    /** Null for a seeded shape, which has no row. */
    id: z.string().uuid().nullable(),
    slug: z.string(),
    origin: z.enum(MEAL_PATTERN_ORIGINS),
    /** Monotonic per slug, starting at 1. Never reused. Null for a seeded shape. */
    version: z.number().int().nullable(),
    status: z.enum(MEAL_PATTERN_STATUSES),
    slot: z.enum(MEAL_PATTERN_SLOTS),
    /** The diets this shape exists for. Never empty — a shape no diet has is inert. */
    dietVariants: z.array(z.enum(DIET_TYPES)),
    /** Empty means universal. A tagged pattern only applies to a meal carrying that tag. */
    cuisines: z.array(z.enum(FOOD_CUISINES)),
    components: z.array(adminMealPatternComponentSchema),
    substitutions: adminMealPatternSubstitutionsSchema.nullable(),
    /**
     * How often a week the shape should recur. **Stated to the model, never
     * enforced** — a week-scoped violation has nowhere to go in the repair
     * pipeline, so this reaches generation as prompt text and nothing else.
     */
    maxPerWeek: z.number().int().nullable(),
    evidence: z.string(),
    /** Who signed this version off. Null on a draft nobody has reviewed. */
    reviewer: z.string().nullable(),
    reviewedAt: z.string().nullable(),
    /** An admin email, `curation:apply` for a version the artifact wrote, or `seed`. */
    createdBy: z.string(),
    createdAt: z.string().nullable(),
    updatedAt: z.string().nullable(),
});
export const adminMealPatternListQuerySchema = z.object({
    status: z.enum(MEAL_PATTERN_STATUSES).optional(),
    slot: z.enum(MEAL_PATTERN_SLOTS).optional(),
    /**
     * Curated rows only. Off by default: the list's job is to show the grammar
     * in force, and the seeded shapes are most of it until somebody curates one.
     */
    curatedOnly: z.coerce.boolean().optional(),
    /**
     * Every version, not only the live one. Off by default: the list is a
     * grammar, and a screen that opened on retired drafts would answer a
     * question nobody asked.
     */
    history: z.coerce.boolean().optional(),
});
// --- Mutations --------------------------------------------------------------
export const adminMealPatternParamsSchema = z.object({
    id: z.string().uuid(),
});
/**
 * Preview a whole slot, not one version.
 *
 * The screen's question is "what would turning this slot's shapes on mean",
 * which the per-id route cannot answer for two reasons. A seeded shape has
 * `id: null` by design — it has no row — so most of the live grammar is not
 * addressable by id at all. And a slot's answer is not the sum of its
 * versions': `wouldReject` is already computed against every live pattern for
 * the slot together, because a meal has to satisfy all of them at once.
 */
export const adminMealPatternSlotPreviewRequestSchema = z.object({
    slot: z.enum(MEAL_PATTERN_SLOTS),
});
/** What a curator states about a shape. Shared by create and by the next version. */
const mealPatternFactsShape = {
    slot: z.enum(MEAL_PATTERN_SLOTS),
    dietVariants: z.array(z.enum(DIET_TYPES)).min(1),
    cuisines: z.array(z.enum(FOOD_CUISINES)).max(4).default([]),
    components: z.array(adminMealPatternComponentSchema).min(1).max(8),
    substitutions: adminMealPatternSubstitutionsSchema.nullable().default(null),
    maxPerWeek: z.number().int().min(1).max(7).nullable().default(null),
    /**
     * Why this is a shape somebody serves. Required and non-trivial: the field
     * this whole surface exists to make somebody fill in. "It is what people
     * eat" is not evidence.
     */
    evidence: z.string().trim().min(10).max(2000),
};
export const adminMealPatternCreateRequestSchema = z.object({
    /**
     * The stable identity across versions, typed rather than derived: unlike a
     * food, a pattern has no name to slugify, and the slug is what a violation
     * message quotes back to the model.
     */
    slug: z
        .string()
        .trim()
        .min(3)
        .max(96)
        .regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, 'Lowercase words joined by single hyphens.'),
    ...mealPatternFactsShape,
});
/**
 * Editing a pattern, which is never an update in place.
 *
 * Every fact is required rather than patchable: a version is a whole
 * judgement somebody signed, and a partial edit would produce a version whose
 * unstated halves were inherited from one nobody re-read. `slug` is absent —
 * it is the identity the versions share.
 */
export const adminMealPatternUpdateRequestSchema = z.object(mealPatternFactsShape);
/**
 * Activating retires whatever version of the same slug was live, so it needs
 * the same accountability an edit does: who is signing this one.
 */
export const adminMealPatternActivateRequestSchema = z.object({
    /**
     * Whether an expert signed it, or only we did. `expert_reviewed` is a
     * stronger claim than `active_provisional` and the console must not make it
     * on somebody's behalf.
     */
    status: z.enum(LIVE_MEAL_PATTERN_STATUSES),
    reviewer: z.string().trim().min(2).max(120),
});
export const adminMealPatternRetireRequestSchema = z.object({
    reason: z.string().trim().min(3).max(280),
});
/**
 * What activating this shape would mean, measured against weeks that already
 * exist.
 *
 * Committed plan days, not a simulation: the question an operator actually has
 * before turning a rule on is "how much of what we already serve would this
 * have rejected", and the only honest answer is the real weeks. A pattern
 * fires on a meal with an item it has no place for, so `wouldReject` is the
 * number that matters and `matched` is its denominator's other half.
 */
export const adminMealPatternPreviewSchema = z.object({
    /** How many committed weeks the sample was drawn from. */
    weeks: z.number().int(),
    /** Meals in this pattern's own slot across those weeks. */
    mealsInSlot: z.number().int(),
    /** Meals this shape has room for exactly as they stand. */
    matched: z.number().int(),
    /**
     * Meals carrying something no *live* pattern for the slot has room for,
     * counted with this shape included. Patterns are alternatives, so this can
     * only fall as shapes are added.
     */
    wouldReject: z.number().int(),
    /** The same count without this shape, so the difference is legible. */
    wouldRejectWithout: z.number().int(),
    /**
     * Up to five real examples, newest first — a count with no example is a
     * number an operator cannot act on.
     */
    samples: z.array(z.object({
        dayOfWeek: z.string(),
        weekStartDate: z.string(),
        roles: z.array(z.string()),
    })),
});
//# sourceMappingURL=meal-patterns.js.map